import React, { useState, useEffect, useRef } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import AccordionBox from "components/Layouts/AccordionBox";
import Row from "./Row";
import API from "helpers/api";
import useDidMountEffect from "hooks/useDidMountEffect";
import usePrevious from "hooks/usePrevious";

const successColor = "#24BA78";
const errorColor = "#E21313";

const media = "@media (max-width: 414px)";

const useStyles = makeStyles()((theme) => ({
	box: {
		marginTop: 16,
	},
	tableContainer: {
		[media]: {
			whiteSpace: "nowrap",
		},
	},
	tableHead: {
		backgroundColor: "#D2D2D9",
		border: "1px solid",
		width: "100%",
	},
}));

const header = (title, errorData, errors, elementID) => (
	<span>
		{title} &nbsp;&nbsp;
		{errors?.total > 0 && (
			<span
				style={{
					color: errors.total === errors.resolved ? successColor : errorColor,
				}}
			>
				{elementID !== "roleID" && `(${errorData} Errors Resolved)`}
			</span>
		)}
	</span>
);

const Elements = ({
	name,
	title,
	errors,
	getApi,
	patchApi,
	siteAppID,
	mainData = [],
	errorName,
	modelName,
	elementID,
	onErrorResolve,
	disableInput,
	access,
	translations,
}) => {
	const showTranslationColumn =
		translations && Object.keys(translations).length > 0;
	const { classes, cx } = useStyles();
	const [dropDown, setDropDown] = useState([]);
	const [updatedDetails, setUpdatedDetails] = useState({
		data: mainData,
		selectedRow: 0,
	});

	useEffect(() => {
		if (siteAppID) {
			API.get(getApi + `?siteAppId=${siteAppID}`).then((res) =>
				setDropDown(res.data)
			);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteAppID]);
	const [prevData, setPreviousValue] = usePrevious(updatedDetails);

	const setErrorResolve = (datas, y) => {
		// If input value is null i.e. deleted then subtract else add

		const uData = updatedDetails.data.map((x) => {
			return x.id === y.id ? { ...x, ...datas } : x;
		});
		setUpdatedDetails({ data: uData, selectedRow: y.id });
	};
	useDidMountEffect(() => {
		const uData = prevData.data.map((x) => {
			return x.id === updatedDetails.selectedRow
				? updatedDetails.data.find((y) => y.id === x.id)
				: x;
		});
		setPreviousValue({ data: uData, selectedRow: updatedDetails.selectedRow });
		onErrorResolve(uData, modelName, errorName, elementID);
	}, [updatedDetails]);

	// Auto-match against the destination dropdown using translated names. Runs
	// once when translations arrive and the dropdown is loaded. Targets two
	// row states:
	//   1. Unresolved rows (no ActionID, no NewName) — pick the translated match.
	//   2. Rows that were pre-resolved at upload time to a destination whose
	//      name equals the SOURCE name (the backend's English auto-match) —
	//      re-point them to the translated destination if one exists. Without
	//      this, a row that lit up green pre-translation stays pinned to the
	//      English action and the task ends up displaying the English name.
	// Rows where the user has explicitly picked a different destination, or
	// clicked Add New, are left alone.
	const autoMatchedRef = useRef(false);
	useEffect(() => {
		if (!translations || Object.keys(translations).length === 0) return;
		if (!dropDown || dropDown.length === 0) return;
		if (autoMatchedRef.current) return;
		autoMatchedRef.current = true;

		const norm = (v) => (v ?? "").trim().toLowerCase();
		const dropDownByName = new Map(dropDown.map((d) => [norm(d.name), d]));
		const dropDownById = new Map(dropDown.map((d) => [d.id, d]));

		const matchPromises = updatedDetails.data
			.filter((row) => {
				if (row.newName !== null) return false;
				if (row[elementID] === null) return true;
				const currentDest = dropDownById.get(row[elementID]);
				return currentDest && norm(currentDest.name) === norm(row.name);
			})
			.map((row) => {
				const translated = translations[row.name];
				if (!translated) return null;
				const match = dropDownByName.get(norm(translated));
				if (!match) return null;
				if (match.id === row[elementID]) return null;
				return API.patch(`${patchApi}/${row.id}`, [
					{ op: "replace", path: elementID, value: match.id },
				])
					.then((res) => ({ row, payload: res.data }))
					.catch(() => null);
			})
			.filter(Boolean);

		if (matchPromises.length === 0) return;

		Promise.all(matchPromises).then((results) => {
			const hits = results.filter(Boolean);
			if (hits.length === 0) return;
			const merged = updatedDetails.data.map((x) => {
				const hit = hits.find((r) => r.row.id === x.id);
				return hit ? { ...x, ...hit.payload } : x;
			});
			// Skip the per-row useDidMountEffect dance — we have multiple rows
			// updating at once. Sync local state and bubble straight up to the
			// parent so the error counter recomputes.
			setUpdatedDetails({ data: merged, selectedRow: -1 });
			setPreviousValue({ data: merged, selectedRow: -1 });
			onErrorResolve(merged, modelName, errorName, elementID);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [translations, dropDown]);
	return (
		<AccordionBox
			accordionClass={classes.box}
			defaultExpanded={false}
			title={header(
				title,
				`${errors?.resolved}/${errors?.total}`,
				errors,
				elementID
			)}
		>
			<Table className={classes.tableContainer}>
				<TableHead className={classes.tableHead}>
					<TableRow>
						<TableCell>Source {name}</TableCell>
						{showTranslationColumn && (
							<TableCell>Translated {name}</TableCell>
						)}
						<TableCell>Destination {name} (Please Select)</TableCell>
						<TableCell>Destination {name} (Add New)</TableCell>
						<TableCell>Resolved ?</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{mainData.map((x) => (
						<Row
							key={x.id}
							dropDown={dropDown.map((x) => ({ label: x.name, id: x.id }))}
							x={x}
							patchApi={patchApi}
							setErrorResolve={(data) => setErrorResolve(data, x)}
							elementID={elementID}
							disableInput={disableInput}
							access={access}
							translatedText={
								showTranslationColumn ? translations[x.name] : undefined
							}
							showTranslationColumn={showTranslationColumn}
						/>
					))}
				</TableBody>
			</Table>
		</AccordionBox>
	);
};

export default Elements;
